// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import MainLayout from '@Organisms/MainLayout/MainLayout';
import { LiveDetailsPage, LiveListPage } from '@Pages/';
import { GlobalStreamLongPollingSrv, GlobalStreamSrv } from '@Services/StreamSrv';
import client, { queryClient } from '@Client';
import Routing from './Routing';
import { useCurrentUser } from '@Queries/userQueries';
import { RouteType } from '@Types';
import { PrincipalMenu } from '@Organisms/MainLayout/PrincipalMenu/PrincipalMenu';
import { getAccessibleRoutes } from '@Helpers/getAccessibleRoutes';
import Routes from '@Pages/routes';
import LoginRoutes from '@Pages/Login/routes';
import RouteBuilder from '@Services/RouteBuilder';
import { useMemo } from 'react';
import { computeEventList } from '@Organisms/User/eventList';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError } from '@Helpers/notification';
import { BrandingParams } from 'thehive-sdk';
import { useGenericQuery } from '@Queries/genericQueries';
import { useCallback } from 'react';
import { ScopeProvider } from '@Context/ScopeProvider';
import { ConfigProvider } from 'antd';
import { LanguageSwitcherContext } from '@Lang/LanguageSwitcherContext';
import StatusProvider from '@Context/StatusProvider';
import { IconsViewer } from '@Pages/viewer/Icons';
import { installIcons } from '@Constants/iconsApp';
import { StreamProvider, destroyModal } from '@Context/StreamProvider';
import { AutoLogoutProvider } from '@Context/AutoLogoutContext';

import './App.scss';

client.addInterceptor(
    (response: any) => response,
    (error: any) => {
        if ([401, 500].includes(error.response.status)) {
            queryClient.refetchQueries('currentUser');
        }
        return Promise.reject(error);
    },
);

const App = (): ReactElement => {
    const i = useLocalMessage();

    const [allRoutes, setAllRoutes] = useState<RouteType[]>();
    const [accessibleRoutes, setAccessibleRoutes] = useState<RouteType[]>();
    const { direction } = useContext(LanguageSwitcherContext);

    const { data: brandingData, status: brandingStatus } = useGenericQuery<BrandingParams>({
        fetchOnStart: true,
        manual: true,
        retry: false,
        key: ['branding-props'],
        fetchCallback: useCallback(() => client.BrandingController.list(), []),
    });

    const { data: currentUserData, error } = useCurrentUser();

    const isAuthentified = !error && !!currentUserData;

    const userEventList = useMemo(
        () => computeEventList({ locale: i, controller: client.UserController, onError: handleNotificationError }),
        [i],
    );

    useEffect(() => {
        installIcons();
    }, []); //eslint-disable-line

    useEffect(() => {
        if (brandingStatus === 'success') {
            if (brandingData && brandingData?.favicon) {
                const favicon = document.querySelector('[rel="icon"]');
                favicon && favicon.setAttribute('href', brandingData?.favicon);
            }
        }
    }, [brandingData, brandingStatus]);

    useEffect(() => {
        if (!isAuthentified) {
            destroyModal();
            GlobalStreamSrv.cancel();
            GlobalStreamLongPollingSrv.cancelFeed();
        }
    }, [brandingData, isAuthentified]);

    useEffect(() => {
        const routes = Object.values(Routes).reduce(
            (acc, currentRoute): RouteType[] => [...acc, ...currentRoute],
            [] as RouteType[],
        );
        setAllRoutes(routes);
    }, []);

    useEffect(() => {
        if (currentUserData && allRoutes) {
            const routes = getAccessibleRoutes(currentUserData, allRoutes);
            setAccessibleRoutes(routes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserData]);

    return (
        <ConfigProvider direction={direction}>
            <Switch>
                <Route path="/icons">
                    <IconsViewer />
                </Route>
                {error && (
                    <Switch>
                        {LoginRoutes.map(({ path, content, exact }) => (
                            <Route path={path} key={path} exact={exact}>
                                {content}
                            </Route>
                        ))}
                        <Route path="*">
                            <Redirect to={RouteBuilder.compute('login.default')} />
                        </Route>
                    </Switch>
                )}

                {isAuthentified && (
                    <StatusProvider>
                        <AutoLogoutProvider>
                            <StreamProvider>
                                <Switch>
                                    <Route path={RouteBuilder.compute('live.details')} exact>
                                        <ScopeProvider>
                                            <LiveDetailsPage brandingData={brandingData} />
                                        </ScopeProvider>
                                    </Route>
                                    <Route path={RouteBuilder.compute('live.default')} exact>
                                        <ScopeProvider>
                                            <LiveListPage brandingData={brandingData} />
                                        </ScopeProvider>
                                    </Route>
                                    <ScopeProvider>
                                        <MainLayout
                                            brandingData={brandingData}
                                            userEventList={userEventList}
                                            menu={
                                                currentUserData &&
                                                accessibleRoutes && (
                                                    <PrincipalMenu
                                                        routes={accessibleRoutes}
                                                        currentUser={currentUserData}
                                                        userEventList={userEventList}
                                                    />
                                                )
                                            }
                                            userData={currentUserData}
                                        >
                                            {accessibleRoutes && <Routing routes={accessibleRoutes} />}
                                        </MainLayout>
                                    </ScopeProvider>
                                </Switch>
                            </StreamProvider>
                        </AutoLogoutProvider>
                    </StatusProvider>
                )}
            </Switch>
        </ConfigProvider>
    );
};

export default App;
